import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  Divider,
  Checkbox,
  FormGroup,
  FormLabel,
  Alert,
  MenuItem
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import roleService from '../../services/roleService';
import permissionService from '../../services/permissionService';
import storeService from '../../services/storeService';
import { useStore } from '../../context/StoreContext';

const RoleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEdit = Boolean(id);
  const { currentStore, isSuperAdmin } = useStore();

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    description: '',
    storeId: currentStore?.id || null,
    permissionIds: [],
    isActive: true
  });

  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [availableStores, setAvailableStores] = useState([]);
  const [loading, setLoading] = useState(false);

  const permissionsByResource = availablePermissions.reduce((acc, permission) => {
    const resource = permission.resource || 'Other';
    if (!acc[resource]) acc[resource] = [];
    acc[resource].push(permission);
    return acc;
  }, {});

  useEffect(() => {
    fetchPermissions();
    if (isSuperAdmin) fetchStores();
    if (isEdit) {
      fetchRole();
    } else {
      if (currentStore) {
        setFormData(prev => ({ ...prev, storeId: currentStore.id }));
      }
    }
  }, [id, currentStore]);

  const fetchStores = async () => {
    try {
      const data = await storeService.getAllStores();
      setAvailableStores(data);
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    }
  };

  const fetchPermissions = async () => {
    try {
      const data = await permissionService.getActivePermissions();
      setAvailablePermissions(data);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
    }
  };

  const fetchRole = async () => {
    try {
      const data = await roleService.getRoleById(id);
      setFormData({
        name: data.name,
        displayName: data.displayName,
        description: data.description || '',
        storeId: data.storeId,
        permissionIds: data.permissions?.map(p => p.id) || [],
        isActive: data.isActive
      });
    } catch (error) {
      toast.error(t('common.loadError'));
      console.error(error);
    }
  };

  const handlePermissionToggle = (permissionId) => {
    setFormData(prev => ({
      ...prev,
      permissionIds: prev.permissionIds.includes(permissionId)
        ? prev.permissionIds.filter(id => id !== permissionId)
        : [...prev.permissionIds, permissionId]
    }));
  };

  const handleSelectAllInResource = (resource) => {
    const resourcePermissions = permissionsByResource[resource].map(p => p.id);
    const allSelected = resourcePermissions.every(id => formData.permissionIds.includes(id));

    if (allSelected) {
      setFormData(prev => ({
        ...prev,
        permissionIds: prev.permissionIds.filter(id => !resourcePermissions.includes(id))
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        permissionIds: [...new Set([...prev.permissionIds, ...resourcePermissions])]
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (isEdit) {
        await roleService.updateRole(id, formData);
        toast.success(t('roles.updateSuccess'));
      } else {
        await roleService.createRole(formData);
        toast.success(t('roles.createSuccess'));
      }
      navigate('/roles');
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {isEdit ? t('roles.editRole') : t('roles.createRole')}
      </Typography>

      {!currentStore && !isSuperAdmin && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('roles_ext.noStoreWarning')}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('roles.role')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={isEdit}
                  helperText={t('roles_ext.nameHelper')}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('common.displayName')}
                  value={formData.displayName}
                  onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                  required
                  helperText={t('roles_ext.displayNameHelper')}
                />
              </Grid>

              {isSuperAdmin && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('roles_ext.storeAssignment')}
                    value={formData.storeId || ''}
                    onChange={(e) => setFormData({ ...formData, storeId: e.target.value || null })}
                    helperText={t('roles_ext.storeAssignmentHelper')}
                  >
                    <MenuItem value="">
                      <em>{t('roles_ext.globalTemplateNoStore')}</em>
                    </MenuItem>
                    {availableStores.map((store) => (
                      <MenuItem key={store.id} value={store.id}>
                        {store.name} ({store.code})
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('common.description')}
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label={t('common.active')}
                />
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  {t('roles_ext.permissionsSelected', { count: formData.permissionIds.length })}
                </Typography>
              </Grid>

              {Object.entries(permissionsByResource).map(([resource, permissions]) => (
                <Grid item xs={12} md={6} key={resource}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <FormLabel component="legend" sx={{ textTransform: 'capitalize', fontWeight: 600 }}>
                          {resource}
                        </FormLabel>
                        <Button
                          size="small"
                          onClick={() => handleSelectAllInResource(resource)}
                        >
                          {permissions.every(p => formData.permissionIds.includes(p.id))
                            ? t('roles_ext.deselectAll')
                            : t('roles_ext.selectAll')}
                        </Button>
                      </Box>
                      <FormGroup>
                        {permissions.map((permission) => (
                          <FormControlLabel
                            key={permission.id}
                            control={
                              <Checkbox
                                checked={formData.permissionIds.includes(permission.id)}
                                onChange={() => handlePermissionToggle(permission.id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">{permission.displayName}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {permission.name}
                                </Typography>
                              </Box>
                            }
                          />
                        ))}
                      </FormGroup>
                    </CardContent>
                  </Card>
                </Grid>
              ))}

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button onClick={() => navigate('/roles')} disabled={loading}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? t('common.saving') : isEdit ? t('common.update') : t('common.create')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default RoleForm;
